import React, { useContext } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import { ThemeContext } from 'src/store/ContextStore';

// Shared danger (delete) hover colours — matches the reference "Delete Family"
// action on the Pedigrees table.
const DELETE_HOVER_COLOR = '#e41a52';
const DELETE_HOVER_BG = 'rgba(228,26,82,0.08)';

/**
 * Consistent table/action icon button used across the whole system.
 *
 * Formatting rules (kept in ONE place for consistency):
 *  - `size="small"` so the round hover background fits inside a table row
 *    (a large button's hover circle was being clipped top/bottom).
 *  - Default icons render in the appearance accent/button colour and, on hover,
 *    show a low-opacity accent circle (same treatment as "View Pedigree").
 *  - `type="delete"` (or `danger`) uses the shared red danger scheme.
 *  - An explicit `#hex` `color` is honoured for non-delete icons.
 */
export default function CustomIconBtn(props) {
  const { type, onClick, size } = props;
  const { theme } = useContext(ThemeContext) || {};
  const accent = theme?.buttonStyle || theme?.accent || '#2ba9bf';

  const isDelete = type === 'delete' || props.danger;
  const explicit =
    !isDelete && typeof props.color === 'string' && props.color.startsWith('#')
      ? props.color
      : null;
  const baseColor = explicit || accent;

  const restColor = isDelete ? 'action.active' : baseColor;
  const hoverColor = isDelete ? DELETE_HOVER_COLOR : baseColor;
  const hoverBg = isDelete ? DELETE_HOVER_BG : alpha(baseColor, 0.12);

  // Icons inherit the button colour so the scheme above applies uniformly.
  const options = { fontSize: size ?? 'small', color: 'inherit' };

  let icon = '',
    tooltip = '',
    disabled = props.disabled ?? undefined;
  switch (type) {
    case 'add': {
      tooltip = 'Add';
      icon = <AddCircleIcon {...options} />;
      break;
    }
    case 'edit': {
      tooltip = 'Edit';
      icon = <EditIcon {...options} />;
      break;
    }
    case 'delete': {
      tooltip = 'Delete';
      icon = <DeleteIcon {...options} />;
      break;
    }
    case 'view': {
      tooltip = 'View';
      icon = <VisibilityIcon {...options} />;
      break;
    }
    case 'publish': {
      tooltip = 'Publish';
      icon = <PublishIcon {...options} />;
      break;
    }
    case 'unpublish': {
      tooltip = 'Unpublish';
      icon = <UnpublishedIcon {...options} />;
      break;
    }
    case 'view-pedigree': {
      tooltip = 'View';
      icon = <AccountTreeIcon {...options} />;
      break;
    }
    case 'imported': {
      tooltip = 'Mark Family As Imported';
      icon = <ImportExportIcon {...options} />;
      break;
    }
    case 'loading': {
      disabled = true;
      icon = <CircularProgress size={15} />;
      break;
    }
    default: {
      tooltip = props.tooltip;
      icon = props.icon;
    }
  }
  return (
    <Tooltip title={props.tooltip ?? tooltip} placement="top" arrow>
      <span>
        <IconButton
          onClick={onClick}
          disabled={disabled}
          {...(props['data-va-anchor']
            ? { 'data-va-anchor': props['data-va-anchor'] }
            : {})}
          size="small"
          sx={{
            color: restColor,
            '&:hover': { color: hoverColor, backgroundColor: hoverBg }
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}
